import { reflect } from '@effector/reflect';
import { Button, TextInput } from '@mantine/core';
import { combine } from 'effector';
import { useUnit } from 'effector-react';
import { not } from 'patronum';
import { createBlockCashdeskModal } from 'features/block-cashdesk';
import { createDeleteCashdeskModal } from 'features/delete-cashdesk';
import { createDepositCashdeskModal } from 'features/deposit-cashdesk';
import { createUnblockCashdeskModal } from 'features/unblock-cashdesk';
import { createWithdrawCashdeskModal } from 'features/withdraw-cashdesk';
import {
	CASHDESK_NAME_MAX_LENGTH,
	CASHDESK_NAME_MIN_LENGTH,
	CASHDESK_NOTE_MAX_LENGTH,
} from 'entities/cashdesk';
import { viewerModel } from 'entities/viewer';
import { translated, $t } from 'shared/lib';
import { MultiSelect } from 'shared/ui';
import { cashdeskPageModel } from '../../model';
import cls from './cashdesk-page-form.module.scss';

const DepositCashdeskModal = createDepositCashdeskModal(cashdeskPageModel.depositCashdeskModel);

const WithdrawCashdeskModal = createWithdrawCashdeskModal(cashdeskPageModel.withdrawCashdeskModel);

const BlockCashdeskModal = createBlockCashdeskModal(cashdeskPageModel.blockCashdeskModel);

const UnblockCashdeskModal = createUnblockCashdeskModal(cashdeskPageModel.unblockCashdeskModel);

const DeleteCashdeskModal = createDeleteCashdeskModal(cashdeskPageModel.deleteCashdeskModel);

const NameField = reflect({
	view: TextInput,
	bind: {
		value: cashdeskPageModel.form.fields.name.$value,
		onChange: (e) => cashdeskPageModel.form.fields.name.change(e.target.value),
		label: translated('cashdesk-page.name'),
		placeholder: translated('cashdesk-page.name'),
		errorProps: { 'data-test-id': 'cashdesk-name-input-error' },
		error: combine(
			[cashdeskPageModel.form.fields.name.$errors, $t],
			([errors, t]) =>
				errors.length > 0 &&
				t(`errors:validation.${errors[0]}`, {
					fieldName: t('add-cashdesk.name'),
					maxLength: CASHDESK_NAME_MAX_LENGTH,
					minLength: CASHDESK_NAME_MIN_LENGTH,
				}),
		),
		readOnly: not(viewerModel.$canWriteCashdesks),
		required: true,
	},
});

const AccountField = reflect({
	view: TextInput,
	bind: {
		value: cashdeskPageModel.form.fields.account.$value,
		label: translated('cashdesk-page.account'),
		readOnly: true,
	},
});

const CurrencyField = reflect({
	view: TextInput,
	bind: {
		value: cashdeskPageModel.form.fields.currency.$value,
		label: translated('cashdesk-page.currency'),
		readOnly: true,
	},
});

const BalanceField = reflect({
	view: TextInput,
	bind: {
		value: cashdeskPageModel.form.fields.balance.$value,
		label: translated('cashdesk-page.balance'),
		readOnly: true,
	},
});

const CashiersField = reflect({
	view: MultiSelect,
	bind: {
		'data-test-id': 'cashdesk-page-cashiers-select',
		value: cashdeskPageModel.form.fields.cashierIds.$value.map((ids) => ids.map(String)),
		onChange: (value) => cashdeskPageModel.form.fields.cashierIds.change(value.map(Number)),
		label: translated('cashdesk-page.cashiers'),
		placeholder: translated('cashdesk-page.cashiers'),
		nothingFoundMessage: translated('cashdesk-page.cashiers-not-found'),
		readOnly: not(viewerModel.$canWriteCashdesks),
		data: cashdeskPageModel.$cashiers,
		fullWidth: true,
	},
});

const NoteField = reflect({
	view: TextInput,
	bind: {
		value: cashdeskPageModel.form.fields.note.$value,
		onChange: (e) => cashdeskPageModel.form.fields.note.change(e.target.value),
		label: translated('cashdesk-page.note'),
		placeholder: translated('cashdesk-page.note'),
		readOnly: not(viewerModel.$canWriteCashdesks),
		errorProps: { 'data-test-id': 'cashdesk-note-input-error' },
		error: combine(
			[cashdeskPageModel.form.fields.note.$errors, $t],
			([errors, t]) =>
				errors.length > 0 &&
				t(`errors:validation.${errors[0]}`, {
					fieldName: t('add-cashdesk.note'),
					maxLength: CASHDESK_NOTE_MAX_LENGTH,
				}),
		),
	},
});

const SaveButton = reflect({
	view: Button,
	bind: {
		type: 'submit',
		disabled: cashdeskPageModel.form.$pristine,
		children: translated('cashdesk-page.save'),
		loading: cashdeskPageModel.editCashdeskMutation.$pending,
		className: cls.cashdeskPageFormFooterOption,
	},
});

export const CashdeskPageForm = () => {
	const [cashdesk, submit] = useUnit([cashdeskPageModel.$cashdesk, cashdeskPageModel.form.submit]);

	return (
		cashdesk !== null && (
			<>
				<form
					className={cls.cashdeskPageForm}
					onSubmit={(event) => {
						event.preventDefault();
						submit();
					}}
				>
					<NameField />
					<AccountField />
					<CurrencyField />
					<div className={cls.cashdeskPageFormBalance}>
						<BalanceField />
						<div className={cls.cashdeskPageFormBalanceOptions}>
							<DepositCashdeskModal.Button
								modalData={cashdesk}
								className={cls.cashdeskPageFormBalanceOption}
							/>
							<WithdrawCashdeskModal.Button
								modalData={cashdesk}
								className={cls.cashdeskPageFormBalanceOption}
							/>
						</div>
					</div>
					<CashiersField />
					<NoteField />
					<div className={cls.cashdeskPageFormFooter}>
						<SaveButton />
						<div className={cls.cashdeskPageFormFooterOptions}>
							{cashdesk.isLocked ? (
								<UnblockCashdeskModal.Button
									modalData={cashdesk}
									className={cls.cashdeskPageFormFooterOption}
								/>
							) : (
								<BlockCashdeskModal.Button
									modalData={cashdesk}
									className={cls.cashdeskPageFormFooterOption}
								/>
							)}
							<DeleteCashdeskModal.Button
								modalData={cashdesk}
								className={cls.cashdeskPageFormFooterOption}
							/>
						</div>
					</div>
				</form>
				<DepositCashdeskModal />
				<WithdrawCashdeskModal />
				<BlockCashdeskModal />
				<DeleteCashdeskModal />
			</>
		)
	);
};
